import { debug } from './debug';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    context: any;
  }
}

interface RefererInfo {
  referer: string;
  reachedTop: boolean;
  isAmp: boolean;
}

/**
 * Detects the top url in the nav bar, whether it's in an AMP iframe, and whether it was able to
 * reach the top most window (if for example it was embedded in several iframes),
 * Adapted from Prebid's referrer detection module
 *
 * Resources:
 * https://github.com/ampproject/amphtml/blob/master/ads/README.md#available-information-to-the-ad
 * https://github.com/prebid/Prebid.js/blob/c696e0019d3120e1c2b4d96ba16f263c3ee1c0f2/src/refererDetection.js
 */

export function detectReferer(win: Window): () => RefererInfo {
  /**
   * This function would return a read-only array of hostnames for all the parent frames.
   * win.location.ancestorOrigins is only supported in webkit browsers. For non-webkit browsers it will return undefined.
   */
  function getAncestorOrigins(winn: Window): DOMStringList | undefined {
    try {
      if (!winn.location.ancestorOrigins) {
        return undefined;
      }

      return winn.location.ancestorOrigins;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * Walk up the windows to get the origin stack and best available referrer
   */
  function refererInfo(): RefererInfo {
    const ancestors = getAncestorOrigins(win);
    let currentWindow;
    let bestReferrer;
    let reachedTop = false;
    let level = 0;
    let valuesFromAmp = false;
    let inAmpFrame = false;

    do {
      const previousWindow = currentWindow;
      const wasInAmpFrame = inAmpFrame;
      let currentLocation;
      let crossOrigin = false;
      let foundReferrer = null;

      inAmpFrame = false;
      currentWindow = currentWindow ? currentWindow.parent : win;

      try {
        currentLocation = currentWindow.location.href || null;
      } catch (e) {
        crossOrigin = true;
      }

      if (crossOrigin) {
        if (wasInAmpFrame) {
          const { context } = previousWindow!;

          try {
            foundReferrer = context.sourceUrl;
            bestReferrer = foundReferrer;

            valuesFromAmp = true;

            if (currentWindow === win.top) {
              reachedTop = true;
            }
          } catch (e) {
            /* Do nothing */
          }
        } else {
          debug(
            'Trying to access cross domain iframe. Continuing without referrer and location'
          );

          try {
            const { referrer } = previousWindow!.document;

            if (referrer) {
              foundReferrer = referrer;

              if (currentWindow === win.top) {
                reachedTop = true;
              }
            }
          } catch (e) {
            /* Do nothing */
          }

          if (!foundReferrer && ancestors?.[level - 1]) {
            foundReferrer = ancestors[level - 1];
          }

          if (foundReferrer && !valuesFromAmp) {
            bestReferrer = foundReferrer;
          }
        }
      } else {
        if (currentLocation) {
          foundReferrer = currentLocation;
          bestReferrer = foundReferrer;
          valuesFromAmp = false;

          if (currentWindow === win.top) {
            reachedTop = true;
          }
        }

        if (currentWindow.context?.sourceUrl) {
          inAmpFrame = true;
        }
      }

      level += 1;
    } while (currentWindow !== win.top);

    return {
      referer: bestReferrer || null,
      reachedTop,
      isAmp: valuesFromAmp,
    };
  }

  return refererInfo;
}

export const getRefererInfo = detectReferer(window);

export const getCleanUrl = (): string => {
  const { referer } = getRefererInfo();
  return referer.split(/[?#]/)[0];
};

export const getUrl = (): string => {
  const { referer } = getRefererInfo();
  return referer;
};

export const getDomain = (): string => {
  const domain = window.location.hostname;
  return domain.replace(/^(www\.)/, '');
};
